<script lang="ts" setup>
import type ResponseData from '@/types/ResponseData'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import type Headquarters from '@/types/Headquarters'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { useRouter } from "vue-router";
import { onBeforeMount, ref, computed } from 'vue';
import store from '@/store'
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import HeadquartersDataService from '@/services/HeadquartersDataService'

// UI primitives
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'

// Simple interface for order items
interface OrderItem {
    description: string;
    [key: string]: unknown;
}

const router = useRouter();

const currentOrder = computed(() => store.state.currentOrder);
const orderedAddress = ref({} as Address);
const currentCompany = ref({} as Company);
const items = ref([] as OrderItem[]);
const permanentResidenceItem = ref();
const permanentResidenceItemPackage = ref();
const priorityItem = ref();
const addressChangeItem = ref();
const vhqs = ref([] as Headquarters[]);

const packages = [
    "Základný trvalý pobyt v Bratislave",
    "Najlepší trvalý pobyt v Bratislave"
];

async function getAllVhqs() {
    await HeadquartersDataService.getAllVirtualHeadquarters()
        .then((response: ResponseData) => {
            vhqs.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: number) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyAddress(id: number) {
    if (id > 0) {
        await AddressDataService.getAddressById(id)
            .then(async (response: ResponseData) => {
                orderedAddress.value = response.data.data as Address;
            })
            .catch((e: Error) => {
                toast.error('Chyba: ' + e.message)
            })
    }
}

async function refreshData() {
    await getCompany(currentOrder.value.company_id);

    // Initialize package from company data if exists
    if (currentCompany.value.sidlo_typ_balika) {
        permanentResidenceItemPackage.value = currentCompany.value.sidlo_typ_balika;
    }

    items.value = JSON.parse(currentOrder.value.items);
    let num = 0;
    items.value.forEach((item) => {
        // Match format: "Služba Trvalý pobyt: {address_id} {name} (platnosť na 1 rok)"
        if (item.description.includes("Služba Trvalý pobyt:") || item.description.includes("Trvalý pobyt:")) {
            // Extract the address ID (first number in the description)
            const match = item.description.match(/:\s*(\d+)/);
            num = match ? parseInt(match[1], 10) : 0;
            // BACKWARD COMPATIBILITY: If num looks like a VHQ id instead of address id, map it
            // This handles orders created before fix (when we stored VHQ id instead of address_id)
            // Complete VHQ to address_id mapping from database
            const vhqToAddressMap: Record<number, number> = {
              1: 1,    // Ružinovská
              2: 2,    // Panorama towers
              3: 3,    // Klingerka
              4: 4,    // Miletička
              5: 5,    // SKY PARK Residence
              6: 308   // Eurovea Tower (the one that caused the bug)
            };
            if (vhqToAddressMap[num]) {
                console.log(`Backward compatibility: Converting old VHQ ID ${num} to address_id ${vhqToAddressMap[num]}`);
                num = vhqToAddressMap[num];
            }
            console.log("Address ID: " + num);
        }
        // Match format: "Balík služieb Trvalého pobytu: Najlepší trvalý pobyt v Bratislave (platnosť na 1 rok)"
        if (item.description.includes("Balík služieb Trvalého pobytu")) {
            permanentResidenceItem.value = item;
            // Extract the package name from the description (text between ': ' and ' (')
            const match = item.description.match(/Balík služieb Trvalého pobytu:\s*([^(]+)/);
            console.log("Package match: ", match);
            if (match && match[1].trim()) {
                permanentResidenceItemPackage.value = match[1].trim();
            } else if (!permanentResidenceItemPackage.value) {
                permanentResidenceItemPackage.value = 'Trvalý pobyt';
            }
        }
        // Match renewal format: "Predlženie služby Najlepší trvalý pobyt v Bratislave (Package) pre ... (platnosť na 1 rok)"
        if (item.description.includes("Predlženie služby") && item.description.includes("pobyt")) {
            permanentResidenceItem.value = item;
            const match = item.description.match(/\(([^)]+)\)/);
            if (match && match[1].trim()) {
                permanentResidenceItemPackage.value = match[1].trim();
            } else if (!permanentResidenceItemPackage.value) {
                permanentResidenceItemPackage.value = 'Trvalý pobyt';
            }
        }
        // Match priority processing upsell: "Prioritné spracovanie objednávky"
        if (item.description.includes("Prioritné spracovanie objednávky")) {
            priorityItem.value = item;
        }
        // Match address change service upsell: "Ohlásenie zmeny pobytu za Vás"
        if (item.description.includes("Ohlásenie zmeny pobytu za Vás")) {
            addressChangeItem.value = item;
        }
    });
    if (num > 0) {
        await getCompanyAddress(num);
    } else if (currentCompany.value.headquarters_id) {
        // Renewal orders don't embed an address ID in the item description,
        // so fall back to the company's current headquarters address.
        try {
            const hqResponse = await HeadquartersDataService.getHeadquartersById(currentCompany.value.headquarters_id);
            const headquarters = hqResponse.data.data as Headquarters;
            if (headquarters.address_id) {
                await getCompanyAddress(headquarters.address_id);
            }
        } catch (e) {
            console.error('Error fetching headquarters address:', e);
        }
    }
}

async function updateCompany() {
    // Update the sidlo_typ_balika with the selected package
    currentCompany.value.sidlo_typ_balika = permanentResidenceItemPackage.value;

    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .then(() => {
            router.go(0);
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
        });
}

onBeforeMount(async () => {
    await getAllVhqs();
    await refreshData();
})
</script>

<template>
    <Card class="max-w-2xl mx-auto flex flex-col gap-6">
        <!-- Header -->
        <CardHeader class="flex items-center gap-4 mb-2">
            <CardAction>
                <div class="flex items-center justify-center size-12 rounded-full bg-blue-50 shadow-xs">
                    <svg class="size-7 text-blue-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24" title="Trvalý pobyt">
                        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                            fill="none" />
                        <circle cx="9" cy="9" r="2" stroke="currentColor" stroke-width="2" fill="none" />
                        <path d="M9 11v5M12 13h3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </div>
            </CardAction>
            <div>
                <CardTitle class="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight mb-0 dark:text-white/90">Trvalý pobyt</CardTitle>
            </div>
            <CardAction>
                <span v-if="permanentResidenceItem?.price" class="ml-2 text-xl font-semibold text-blue-600"
                    title="Cena balíka">- {{ permanentResidenceItem?.price }}€</span>
            </CardAction>
        </CardHeader>

        <!-- Address Section -->
        <CardContent class="flex flex-col gap-2 bg-gray-50 rounded-lg p-4 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Adresa trvalého pobytu</label>
            <select id="vhqs" name="vhqs" disabled
                class="bg-gray-100 border border-gray-200 rounded-md px-2 py-1 text-gray-700 cursor-not-allowed h-11 w-full min-w-0 rounded-[10px] border-gray-300 text-sm font-normal text-gray-800 shadow-theme-xs outline-hidden transition-colors placeholder:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100/50 disabled:text-gray-400 disabled:shadow-none dark:bg-gray-900 dark:border-gray-800 dark:text-gray-300 dark:border-gray-700 dark:text-white/90 dark:placeholder:text-gray-500 dark:disabled:border-gray-800 dark:disabled:bg-gray-800/50">
                <option v-for="vhq in vhqs" :key="vhq.address_id" :value="vhq.address_id"
                    :selected="vhq.address_id == orderedAddress.id" class="text-black dark:text-white/90">
                    {{ vhq.name }}
                </option>
            </select>
            <div class="text-sm text-gray-700 mt-1 flex items-center gap-2 dark:text-gray-300">
                <svg class="size-4 text-blue-400" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24">
                    <path d="M17.657 16.657L13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0z" />
                    <circle cx="12" cy="11" r="3" />
                </svg>
                {{ orderedAddress.street + ' ' + orderedAddress.street_number + (orderedAddress.street_number2 ? '/' +
                    orderedAddress.street_number2 : '') + ', ' + orderedAddress.psc + ' ' + orderedAddress.city }}
            </div>
        </CardContent>

        <!-- Package and Payment Section -->
        <CardContent class="flex flex-col gap-4">
            <div class="flex flex-col md:flex-row gap-4">
                <div class="flex-1 flex flex-col gap-1">
<FormKit type="select" v-model="permanentResidenceItemPackage" name="typ" id="typ"
                    :classes="{ outer: 'flex flex-col', input: 'mt-1 h-11 w-full rounded-[10px] border border-gray-300 bg-white px-4 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90' }">
                    <option v-for="pck in packages" :key="pck" :value="pck">{{ pck }}</option>
                </FormKit>
                </div>
                <div class="flex-1 flex flex-col gap-1">
                    <label for="vzaplatenedo" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Balík zaplatený do</label>
                    <Input v-model="currentCompany.sidlo_zaplatene_do" name="vzaplatenedo" id="vzaplatenedo" />
                </div>
            </div>
            <div class="flex flex-row justify-end">
                <Button type="button" @click="updateCompany()" class="mt-2 transition-all duration-200" title="Uložiť zmeny">
                    <template #icon>
                        <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </template>
                    Uložiť
                </Button>
            </div>
            <p><b>Prosim <u>prever</u> či sa trvalý pobyt aktualizoval správne.</b></p>
        </CardContent>

        <!-- Additional Services Section -->
        <Alert v-if="priorityItem || addressChangeItem" variant="info" class="flex flex-col gap-2 bg-gray-50 rounded-lg p-4 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
            <AlertTitle class="block text-sm font-medium text-gray-700 dark:text-gray-300">Doplnkové služby</AlertTitle>
            <div v-if="priorityItem" class="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                <AlertDescription class="inline-flex items-center gap-2">
                    <svg class="size-4 text-blue-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Prioritné spracovanie objednávky
                </AlertDescription>
                <Badge variant="light" color="info" class="font-semibold">+ {{ priorityItem.price }}€</Badge>
            </div>
            <div v-if="addressChangeItem" class="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                <AlertDescription class="inline-flex items-center gap-2">
                    <svg class="size-4 text-orange-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M17.657 16.657L13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a8 8 0 1 1 11.314 0z" />
                        <circle cx="12" cy="11" r="3" />
                    </svg>
                    Ohlásenie zmeny pobytu za Vás
                </AlertDescription>
                <Badge variant="light" color="warning" class="font-semibold">+ {{ addressChangeItem.price }}€</Badge>
            </div>
        </Alert>
    </Card>
</template>